import React, { useEffect, useMemo, useState } from 'react';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Navbar } from './common/Catalyst/navbar';
import AppSidebar from './AppSidebar';
import { Heading } from './common/Catalyst/heading';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { companyService } from './clients/ApiClients';
import {
  UserType,
  canManageReservations,
  canPostInternalComment,
} from './constants/UserTypes';
import { useUser } from './hooks/UserContext';
import { ActivityPanel } from './dashboard/ActivityPanel';
import { MoneyPanel } from './dashboard/MoneyPanel';
import { buildHomeFeed, moneyHoaIds } from './dashboard/homeMeta';
import { useHomeSources } from './dashboard/useHomeSources';
import { useMoneyAttention } from './dashboard/useMoneyAttention';
import { useSyncedCompany } from './sync/hooks';

interface DashboardProps {
  currentPage: string;
}

// "5 minutes ago" and "overdue" both move with the clock, not the data.
const TICK_MS = 60_000;

const useNow = (): Date => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
};

const greeting = (now: Date): string => {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

/**
 * Home (#670): everything across the viewer's associations, newest first.
 * Overdue work is pinned at the top; below it, what is the viewer's own
 * and everything that changed, side by side. It reads the sync engine's
 * copy, so a change made anywhere — another manager, a homeowner's
 * reply, a job on the server — shows here without a reload. Money is
 * fetched, for those allowed to see it, and says it is not live.
 */
const Dashboard: React.FC<DashboardProps> = ({ currentPage }) => {
  const { userId, userType, companyId, fullName, email } = useUser();
  const now = useNow();
  const { sources, live, loading, errors, retry } = useHomeSources();

  const feed = useMemo(
    () =>
      buildHomeFeed(
        sources,
        {
          userId,
          fullName,
          email,
          decidesReservations: canManageReservations(userType),
        },
        now
      ),
    [sources, userId, fullName, email, userType, now]
  );

  // As a comment thread shows its badge: to whoever may post internal notes.
  const showThreads = canPostInternalComment(userType);

  const hoaIds = useMemo(
    () => moneyHoaIds(sources, userType === UserType.MANAGER),
    [sources, userType]
  );
  const money = useMoneyAttention(hoaIds);
  const hoaName = (id: string): string =>
    sources.hoas.find((h) => h.id === id)?.name ?? 'Association';

  // The sync engine's copy when there is one (#561); fetched otherwise.
  const syncedCompany = useSyncedCompany(companyId);
  const haveSyncedCompany = !!syncedCompany;
  const [fetchedCompanyName, setCompanyName] = useState<string | null>(null);
  const companyName = syncedCompany?.name ?? fetchedCompanyName;
  useEffect(() => {
    if (!companyId || haveSyncedCompany) return;
    let current = true;
    companyService
      .getCompany({ id: companyId })
      .then((r) => current && setCompanyName(r.data.name))
      // Only the subtitle hangs on it; the page is whole without it.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [companyId, haveSyncedCompany]);

  const firstName = fullName?.trim().split(/\s+/)[0];

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Heading>Home</Heading>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[13px] text-fg-muted">
        <span>
          {greeting(now)}
          {firstName ? `, ${firstName}` : ''}
        </span>
        {companyName && (
          <>
            <span aria-hidden>·</span>
            <span>{companyName}</span>
          </>
        )}
        <span aria-hidden>·</span>
        {live ? (
          <span className="inline-flex items-center gap-1.5" title="Changes appear as they happen">
            <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
            Live
          </span>
        ) : (
          <span title="Showing violations, work orders and notifications; the rest appears once live updates connect">
            Not live
          </span>
        )}
      </p>

      {errors.length > 0 && (
        <ErrorAlert className="mt-4" onRetry={retry}>
          {errors.join(' ')}
        </ErrorAlert>
      )}

      {feed.overdue.length > 0 && (
        <div className="mt-6">
          <ActivityPanel
            title="Overdue"
            tone="danger"
            items={feed.overdue}
            now={now}
            showThreads={showThreads}
            pageSize={5}
            emptyMessage="Nothing is overdue."
          />
        </div>
      )}

      <div className="mt-6 grid items-start gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="flex min-w-0 flex-col gap-6">
          <ActivityPanel
            title="For you"
            items={feed.forYou}
            now={now}
            showThreads={showThreads}
            pageSize={8}
            loading={loading}
            emptyMessage="Nothing assigned to you or waiting on you."
          />
          {money.allowed && <MoneyPanel state={money} hoaName={hoaName} />}
        </div>
        <ActivityPanel
          title="Recent activity"
          items={feed.recent}
          now={now}
          showThreads={showThreads}
          pageSize={15}
          loading={loading}
          emptyMessage="No activity yet."
        />
      </div>
    </SidebarLayout>
  );
};

export default Dashboard;
